/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import './NavBarCSS.css';
// JavaScript plugin that hides or shows a component based on your scroll
import Headroom from "headroom.js";
// reactstrap components
import {
  Button,
  Collapse,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Media,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Card,
} from "reactstrap";
import { useUserAuth } from "../../context/UserContext";
import { useNavigate, useLocation } from "react-router";
import { useTranslation, Trans } from "react-i18next";
import { resolveAvatar } from "../../constants/avatars";
import { isPrivilegedRole } from "../../utils/roles";
import { COURSES_OPEN } from "../../config/courses";

const LessonNavBar = () => {
  const [collapseClasses, setCollapseClasses] = useState("");
  const [collapseOpen, setCollapseOpen] = useState(false);
  const { logOut, user } = useUserAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // The floating AI assistant (which snaps into #ai-robot-dock) is disabled on
  // the Claude Course (CC_/CD_/V2_/V3_/V4_) lessons in favor of the in-page Prompt
  // Lab, so hide the empty dock socket there — otherwise it renders as a bare circle.
  //
  // It is also staff-only: AiAssistantGate renders the orb for admins and testers
  // only, so everyone else must not get the empty socket either.
  //
  // This is one of THREE guards that must agree: AiAssistantGate decides whether
  // <AiAssistant> renders at all, and this dock plus DemoNavbar's are the sockets
  // it snaps into. Adding a course — or a role — to only one of them is the bug
  // this comment exists to prevent.
  const { pathname } = useLocation();
  const showAiDock =
    isPrivilegedRole(user?.role) &&
    !pathname.startsWith("/CC_M") &&
    !pathname.startsWith("/CD_M") &&
    !pathname.startsWith("/V2_M") &&
    !pathname.startsWith("/V3_M") &&
    !pathname.startsWith("/V4_M");

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error.message);
    }
  };



  const onExiting = () => {
    setCollapseClasses("collapsing-out");
  };

  const onExited = () => {
    setCollapseClasses("");
  };

  return (
    <>
      <header className="header-global " >
        <Navbar
          className="lessonNavBar"
          expand="lg"

        >

          <Container className="">
            <NavbarBrand className="mr-lg-5 lessonNavBar__brand" to="/" tag={Link}>
              <img
                alt="CodeNec"
                className="lessonNavBar__brand-img"
                src={require("assets/img/brand/CodenecLogoNoLettersTransparent.png")}
              />
            </NavbarBrand>
            <button
              className="navbar-toggler"
              type="button"
              aria-label={t("nav.openMenu")}
              aria-expanded={collapseOpen}
              aria-controls="lesson-nav-collapse"
              onClick={() => setCollapseOpen((open) => !open)}
            >
              <span className="navbar-toggler-icon" />
            </button>
            <Collapse
              id="lesson-nav-collapse"
              isOpen={collapseOpen}
              navbar
              className={collapseClasses}
              onExiting={onExiting}
              onExited={onExited}
            >
              <div className="navbar-collapse-header ">
                <Row>
                  <Col className="collapse-brand " xs="6">
                    <Link to="/">
                      <img
                        alt="Codenec"
                        src={require("assets/img/brand/CodenecLogoNoLettersTransparent.png")}
                      />
                    </Link>
                  </Col>
                  <Col className="collapse-close" xs="6">
                    <button
                      className="navbar-toggler"
                      type="button"
                      aria-label={t("nav.closeMenu")}
                      onClick={() => setCollapseOpen(false)}
                    >
                      <span />
                      <span />
                    </button>
                  </Col>
                </Row>
              </div>
              <Nav className="navbar-nav-hover align-items-lg-center" navbar>

                {/* Hidden while the courses are closed, same as DemoNavbar —
                    admins and testers keep the link. See config/courses.js. */}
                {user && (COURSES_OPEN || isPrivilegedRole(user?.role)) && (
                  <NavItem to="/courses" tag={Link} >
                    {t("nav.Courses")}
                  </NavItem>
                )}



              </Nav>




            </Collapse>




          </Container>
          <Nav className=" navbar-nav-hover align-items-lg-center" navbar>
            {/* <Container md="4" className="border d-flex justify-content-end"> */}

            {/* 🤖 AI Assistant dock slot — hidden on Claude Course lessons,
                where the floating assistant is replaced by the in-page Prompt Lab. */}
            {showAiDock && (
              <NavItem className="ai-robot-nav-item  pr-5">
                <div id="ai-robot-dock" />
              </NavItem>
            )}

            {/* Portal target for the Prompt Lab "Walkthrough" replay pill, which is
                rendered by PromptLabHubComponent (a sibling of this navbar, so it
                cannot place the button here directly). Intentionally NOT behind a
                path guard: it is a bare div with no styling of its own, so it stays
                invisible on pages with no lesson hub to portal into it. */}
            <NavItem className="lesson-walkthrough-nav-item">
              <div id="lesson-walkthrough-dock" />
            </NavItem>

            <UncontrolledDropdown nav>
              <DropdownToggle nav>
                <i className="ni ni-ui-04 d-lg-none mr-1" aria-hidden="true" />

                {/* A span, not a link: the toggle is already the link, and the
                    avatar's alt is its name. */}
                <NavbarBrand tag="span">
                  <img
                    alt={t("nav.Profile")}
                    className="rounded-circle lessonNavBar__profile-img"
                    src={resolveAvatar(user?.avatarId)}
                    style={{ objectFit: "contain", backgroundColor: "#f6f9fc" }}
                  />
                </NavbarBrand>
              </DropdownToggle>

              <DropdownMenu>
                <DropdownItem to="/profile-page" tag={Link}>
                  {t("nav.Profile")}
                </DropdownItem>
                {/* <DropdownItem to="/login-page" tag={Link}>
                  Login
                </DropdownItem>
                <DropdownItem to="/register-page" tag={Link}>
                  Register
                </DropdownItem> */}
                <DropdownItem onClick={handleLogout} tag={Link}>
                  {t("nav.Logout")}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            {/* </Container> */}

          </Nav>
        </Navbar>
      </header>
    </>
  );
};

export default LessonNavBar;
